import { IconNames } from '@blueprintjs/icons';
import { formatDate, sortAsc, sortDateDesc } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { FormSectionWithRows } from '@strata/react-form-section';
import { GridWithSearch, SecondaryData } from '@strata/react-grid';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuggestSingleSelect } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { ProductExpansion } from 'pages/goLives/ProductExpansions';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';
import { mapPayerSegment } from 'services/PayerSegmentSupport';

const goLivesCagSearchUrl = (goLiveId, segRefId, pageNumber, searchText) =>
    `/reference/payer_segments/go_lives/${goLiveId}/paged?segment_ref_id=${segRefId}&page_number=${pageNumber}&search_text=${searchText}`;
const goLivesUrl = (segmentId) => `/go-lives/${segmentId ?? ''}`;
const COUNT_PER_PAGE = 10;

const columns = ({ segmentId, goLive, navigate }) => [
    {
        header: 'Group Name',
        accessorKey: 'group_name',
    },
    {
        header: 'Carrier',
        accessorKey: 'carrier',
    },
    {
        header: 'Account',
        accessorKey: 'account',
    },
    {
        header: 'Group',
        accessorKey: 'benefit_group',
    },
    {
        header: 'Program Start Date',
        accessorKey: 'program_effective_date',
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        header: 'Program End Date',
        accessorKey: 'program_end_date',
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        header: '',
        accessorKey: `cag_id`,
        size: 70,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() => {
                        navigate({
                            pathname: `/go-lives/${segmentId}/${goLive.go_live_id}/cag/${getValue()}/${row.original.program_effective_date}`,
                        });
                    }}
                />
            </div>
        ),
    },
];

const GoLive = ({ goLive, segmentId, navigate }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [searchText, setSearchText] = useState();
    const [includedCAGs, setIncludedCAGs] = useState(goLive.included_cags);

    useEffect(() => {
        void (async () => {
            try {
                const response = await get(
                    goLivesCagSearchUrl(
                        goLive.go_live_id,
                        goLive.segment_ref_id,
                        currentPage,
                        searchText ?? '',
                    ),
                );
                setIncludedCAGs(response);
            } catch (err) {
                handleError('Error fetching go live info', err);
            }
        })();
    }, [currentPage, goLive, searchText]);

    return (
        <FormSectionWithRows
            sectionTitle={goLive.go_live_name}
            rightHeaderContent={
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() => {
                        navigate({
                            pathname: `/go-lives/${segmentId}/${goLive.go_live_id}`,
                        });
                    }}
                />
            }
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        label="Platform Start Date"
                        value={formatDate(goLive.platform_start_date)}
                    />
                    <DetailLabelValuePair
                        label="Outreach Date"
                        value={formatDate(goLive.outreach_date)}
                    />
                    <DetailLabelValuePair
                        label="Go-Live Date"
                        value={formatDate(goLive.go_live_date)}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <div className={classNames('padding__bottom--small', 'font-semi-bold')}>
                        CAG Details
                    </div>
                    <GridWithSearch
                        columns={columns({ segmentId, goLive, navigate })}
                        data={includedCAGs}
                        currentPage={currentPage}
                        totalCount={goLive.cag_count}
                        showRecordCount
                        countPerPage={COUNT_PER_PAGE}
                        onPageChange={(newValue) => setCurrentPage(newValue)}
                        searchText={searchText}
                        onSearch={(newValue) => setSearchText(newValue)}
                        paginationNextToSearch
                        rightHeaderContent={
                            <Button
                                label="Add CAG"
                                intent={Button.Intent.PRIMARY}
                                onClick={() =>
                                    navigate({
                                        pathname: `/go-lives/${segmentId}/${goLive.go_live_id}/cag/${CREATE_ROUTE}`,
                                    })
                                }
                            />
                        }
                    />
                </>
            }
            rowTwoFlexDirection="column"
        />
    );
};

export const GoLives = () => {
    const { segmentId } = useParams();
    const navigate = useNavigate();

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentOptions = useMemo(
        () => payerSegments?.map(mapPayerSegment).sort(({ name: a }, { name: b }) => sortAsc(a, b)),
        [payerSegments],
    );

    const goLives = useGet(
        segmentId ? `/reference/payer_segments/${segmentId}/go_lives_initial_page` : undefined,
    );
    const productExpansions = useGet(
        segmentId ? `/reference/product_expansions?segment_id=${segmentId}` : undefined,
    );
    const sortedGoLives = useMemo(
        () => goLives?.slice().sort((a, b) => sortDateDesc(a.go_live_date, b.go_live_date)),
        [goLives],
    );

    return (
        <div className="sds_list-page">
            <div className="sds_list-page__content--top">
                <SuggestSingleSelect
                    id="segmentId"
                    name="segmentId"
                    label="Payer Segment"
                    required
                    isLoading={!payerSegments}
                    values={payerSegmentOptions}
                    onChange={(newValue) => navigate(goLivesUrl(newValue?.secondaryData))}
                    selectedItem={payerSegmentOptions?.find(
                        (segment) => segment.secondaryData === segmentId,
                    )}
                />
            </div>
            {segmentId && (
                <>
                    <ListPageHeader
                        pageTitle="Go-Lives"
                        buttons={[
                            {
                                label: 'Create Go-Live',
                                path: `/go-lives/${segmentId}/${CREATE_ROUTE}`,
                            },
                        ]}
                    />
                    <div
                        className="flex__gap--small"
                        style={{
                            overflow: 'auto',
                            flexDirection: 'column',
                        }}
                    >
                        {sortedGoLives?.map((goLive) => (
                            <GoLive
                                key={goLive.go_live_id}
                                goLive={goLive}
                                segmentId={segmentId}
                                navigate={navigate}
                            />
                        ))}
                        {productExpansions?.map((expansion) => (
                            <ProductExpansion
                                key={expansion.product_expansion_id}
                                productExpansion={expansion}
                                segmentId={segmentId}
                                navigate={navigate}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

GoLive.propTypes = {
    goLive: PropTypes.shape({
        go_live_id: PropTypes.string.isRequired,
        go_live_name: PropTypes.string.isRequired,
        platform_start_date: PropTypes.string.isRequired,
        outreach_date: PropTypes.string.isRequired,
        go_live_date: PropTypes.string.isRequired,
        included_cags: PropTypes.arrayOf(
            PropTypes.shape({
                carrier: PropTypes.string.isRequired,
                account: PropTypes.string.isRequired,
                benefit_group: PropTypes.string.isRequired,
            }),
        ).isRequired,
        segment_ref_id: PropTypes.string.isRequired,
        cag_count: PropTypes.string,
    }).isRequired,
    segmentId: PropTypes.string.isRequired,
    navigate: PropTypes.func.isRequired,
};
